import React from 'react';
import { Href } from 'redux-little-router';

import { SelectorItems } from '../components/CampaignAndGroupSelector/Selector/Selector';
import { ErrorItem } from '../components/ErrorsPresenter/ErrorsPresenter';
import { RecommendationCardView } from '../components/RecommendationCard/RecommendationCard';
import { PayActionCampaign } from '../service/ConfirmPayAction/ConfirmPayAction';

import {
    campaigns,
    clients,
    DateRangePreset,
    deals,
    groups,
    Lang,
    LinkTarget,
    logs,
    NavLinkData,
    OperatorData,
    pricePackages,
    reportsClient,
    reportsDomain,
    SaveSuccessData,
    Tld
} from './common';
import { RegionCode } from './constants';
import { DealItemAction } from './deals';
import { DocCenterTooltip } from './doc-center';
import {
    CampaignsCalcTypeFilter,
    CampaignType,
    FilterGridViewDict,
    GridRow,
    PhraseChange,
    ScreenAdditionalFilter
} from './domain';
import { FilterPartialExpression } from './filter';
import { GoalDecl } from './goals';
import { CurrencyCode, GdCampaignType } from './graphql-grid';
import { EditGroupFilterItem } from './groupsEditState';
import { LogNavEntityParams } from './log';
import { MinusPhraseLibraryActions } from './minusPhraseLibrary';
import { PcodeRenderData } from './pcode';
import { PricePackagesMenuActions } from './pricePackages';
import { RecommendationKey, RecommendationType } from './recommendations';
import { ReplaceOption, SearchAndReplaceInspectorState, SearchByRadioButton, SearchOption } from './searchAndReplace';
import { CampaignAndGroupSelectorEntityToSelectType } from './sidebar';
import { BannerAimingPopupData } from './state';

export enum NoticeType {
    error = 'error',
    warning = 'warning',
    success = 'success',
    info = 'info'
}

export enum SortDirection {
    asc = 'asc',
    desc = 'desc'
}

export interface SortParams {
    key: string;
    direction: SortDirection;
    goalId?: string;
}

export interface GridStatPeriod {
    statPreset?: DateRangePreset;
    statFrom?: string;
    statTo?: string;
}

export const defaultClientInfo = {
    hasEcommerce: true,
    hasMetrikaCounters: true,
    hasCampaignsWithCPAStrategy: true,
    isWalletEnabled: true,
    workCurrency: CurrencyCode.RUB,
    isBannerUpdateAllowed: false,
    isCpmBannerUpdateAllowed: false,
    isMcBannerDnaEnabled: false,
    isAdGroupUpdateAllowed: false, // флаг отображения массовой операции "редактирование" групп
    isBrandLiftAllowed: false,
    countryRegionId: RegionCode.RUSSIA
};

export enum RowHeights {
    compact = 60,
    default = 80
}

export enum RowHeightsUserLogs {
    default = 110,
    expanded = 120
}

export type GridViewType = keyof GridSelectionInfo;
export type GridContextType = clients | campaigns | groups;

export type ColumnsViewType = GridViewType | logs | deals | reportsClient | reportsDomain | pricePackages;

export interface GridParams extends GridSourceParams {
    additionalFilter?: ScreenAdditionalFilter;
    isOnlyFavoriteCampaignsVisible?: boolean;
}

export enum GridAlertMessageType {
    minusPhraseBindingError = 'minusPhraseBindingError',
    minusPhraseUnbindingError = 'minusPhraseUnbindingError',
    regionsTransformError = 'regionsTransformError'
}

export interface GridSourceParams {
    context: GridContextType;
    view: GridViewType;
    statPeriod?: GridStatPeriod;
    login: string;
    pathname: string;
    campaignsIds?: Array<string>;
    groupsIds?: Array<string>;
    sortParams?: SortParams;
    filter?: FilterPartialExpression[];
    messageType?: GridAlertMessageType;
    goalIds?: Array<string>;
    alertMessageFilter?: FilterPartialExpression[];
    campaignsCalcTypeFilter?: CampaignsCalcTypeFilter;
}

export interface GridScreenAdditionalFilterItem {
    text: string;
    val: string;
}

export interface GridColumnsOptions {
    hasEcommerce: boolean;
    hasMetrikaCounters: boolean;
    hasCampaignsWithCPAStrategy: boolean;
    hasOnlyCpmCampaigns: boolean;
    isWalletEnabled: boolean;
    isBrandLiftAllowed: boolean;
    workCurrency: CurrencyCode;
    clientRegion: string;
    operator: OperatorData;
    currentLang: Lang;
    tld: Tld;
}

export interface GridColumnStaticDeclBase {
    key: string;
    title: string;
    titleSettings?: string;
    isSortable?: boolean;
    width?: number;
    minWidth?: number;
    sortKey?: string;
    maxWidth?: number;
    tooltipKey?: DocCenterTooltip;
    tooltipText?: string;
    defaultSortDirection?: SortDirection;
    align?: 'right' | 'center' | 'left';
    cellClass?: string;
    fixed?: boolean;
    mandatory?: boolean;
    totalKey?: GridScreenTotalKey;
    isAlignedToLeft?: boolean;
}

export interface ActionResult {
    successCount: number;
    skippedCount: number;
    noticeType: NoticeType;
}

export interface GridColumnStaticDecl<T, K = {}> {
    key: string;
    title: string;
    titleSettings?: string;
    isSortable?: boolean;
    width?: number;
    minWidth?: number;
    sortKey?: string;
    maxWidth?: number;
    tooltipKey?: DocCenterTooltip;
    tooltipText?: string;
    // направление сортировки при первом запросе сортировки на этом поле
    defaultSortDirection?: SortDirection;
    align?: 'right' | 'center' | 'left';
    cellClass?: string;
    CellComponent: React.MemoExoticComponent<(props: GridCellProps<T, K>) => JSX.Element | null>;
    fixed?: boolean;
    mandatory?: boolean;
    totalKey?: GridScreenTotalKey;
    isAlignedToLeft?: boolean;
    hintInfo?: string;
}

export enum GridScreenTotalKey {
    name = 'name',
    cost = 'cost',
    clicks = 'clicks',
    ctr = 'ctr',
    shows = 'shows'
}

export interface GridGroupedColumn<T, K = {}> {
    title?: string;
    key: string;
    columns: Array<GridColumnDecl<T, K>>;
    fixed?: boolean;
    goalId?: string;
    isGoalIcon?: boolean;
}

export interface GridColumnDecl<T, K = {}> {
    key: string;
    title: string;
    titleSettings?: string;
    hintInfo?: string;
    isSortable?: boolean;
    width: number;
    widthCompact?: number;
    minWidth?: number;
    maxWidth?: number;
    hidden?: boolean;
    tooltipKey?: DocCenterTooltip;
    tooltipText?: string;
    // направление сортировки при первом запросе сортировки на этом поле
    defaultSortDirection?: SortDirection;
    align?: 'right' | 'center' | 'left';
    cellClass?: string;
    CellComponent: React.MemoExoticComponent<(props: GridCellProps<T, K | undefined>) => JSX.Element | null>;
    fixed?: boolean;
    mandatory?: boolean;
    flexGrow?: number;
    isResizable?: boolean;
    sortKey?: string;
    totalKey?: GridScreenTotalKey;
    isAlignedToLeft?: boolean;
    goalId?: string;
}

export type OptionalGridColumnDecl<T, K, TOptions> = GridColumnStaticDecl<T, K> & {
    isAvailable?: (options: TOptions) => boolean;
};

export interface GridError {
    title: string;
    message: string;
}

export interface GridData {
    data: Array<GridRow>;
    allItemsIds: Array<string>;
    total: number;
    totalStats?: GridTotalStatsInfo;
    cacheKey?: string;
    error?: GridError;
}

export interface GridCampaignContextEntity {
    id: string;
    name: string;
    type: CampaignType;
}

export interface GridGroupContextEntity {
    id: string;
    name: string;
    campaignId: string;
    regionsIds: number[];
    minusKeywords: string[];
}

export type PhraseId = string;
export type GridSelection = PhraseId[];

export type GridSelectionInfo = {
    campaigns: GridSelection;
    groups: GridSelection;
    banners: GridSelection;
    phrases: GridSelection;
    retargetings: GridSelection;
    feedFilters: GridSelection;
    usersProfile: GridSelection;
};

export const getEmptyGridSelectionInfo = (): GridSelectionInfo => {
    return {
        campaigns: [],
        groups: [],
        banners: [],
        phrases: [],
        retargetings: [],
        feedFilters: [],
        usersProfile: []
    };
};

export function getEmptySearchAndReplaceInspectorState(): SearchAndReplaceInspectorState {
    return {
        value: {
            searchValue: '',
            searchOptionValue: SearchOption.SEARCH,
            replaceValue: '',
            replaceOptionValue: ReplaceOption.REPLACE,

            searchByRadioButtonValue: SearchByRadioButton.PHRASE,
            matchCase: false,
            wholeWords: false
        },
        visible: false,
        isPreviewLoading: false,
        isReplaceLoading: false
    };
}

export interface GridContextTitleData {
    text: string;
}

export interface GridContext {
    titleData: GridContextTitleData;
    location: Href;
}

export interface GridContextObjectsInfo {
    clients: GridContext;
    campaigns?: GridContext;
    groups?: GridContext;
}

export interface GridScreenUrlQueryParams {
    sortKey?: string | null;
    sortDirection?: SortDirection | null;
    campaignsIds?: Array<string>;
    groupsIds?: Array<string>;
    statPreset?: string | null;
    calcType?: CampaignsCalcTypeFilter;
    statFrom?: string | null;
    statTo?: string | null;
    filter?: string | null;
    additionalFilter?: ScreenAdditionalFilter | null;
    ulogin?: string;
}

export interface GridScreenUrlRouteParams {
    view?: GridViewType;
}

export enum GridScreenUrlSearchRouteParams {
    view = 'view'
}

export enum GridScreenUrlSearchQueryParams {
    campaignsIds = 'campaigns-ids',
    groupsIds = 'groups-ids',
    sortKey = 'sort-key',
    sortDirection = 'sort-direction',
    sortGoalId = 'sort-goal-id',
    statPreset = 'stat-preset',
    statFrom = 'stat-from',
    statTo = 'stat-to',
    filter = 'filter',
    ulogin = 'ulogin',
    messageType = 'message-type',
    calcType = 'calc-type'
}

export interface GridContextUrlParams {
    login: string;
    contextIds?: Array<string>;
    campaignsIds?: Array<string>;
    campaignsCalcTypeFilter?: CampaignsCalcTypeFilter;
    groupsIds?: Array<string>;
    filter?: string | null;
    statPreset?: DateRangePreset;
    statFrom?: string;
    statTo?: string;
}

export type SortLinkUrlGetter = (sortParams: SortParams) => Href;

export enum GridActionItemType {
    action = 'action',
    nav = 'nav'
}

export type GridActionItem = {
    type: GridActionItemType;
    text: React.ReactNode;
    val: string;
    linkTarget?: LinkTarget;
    disabled?: boolean;
    hidden?: boolean;
};

export type GridActionGroup = {
    title?: string;
    items: Array<GridActionItem>;
};

export type GridActionItems = Array<GridActionGroup>;

export interface GridActionFeatures {
    canBeSentToBSAdGroupsCount: number;
    canRemoderateAdsCalloutsCount: number;
    canAcceptAdsCalloutsModerationCount: number;
    canBeDeletedAdGroupsCount: number;
    canBeSentToModerationAdGroupsCount: number;
    canBeSentToRemoderationAdGroupsCount: number;
    canAcceptModerationAdGroupsCount: number;
    canBeSentToModerationByClientAdGroupsCount: number;
    canBeCopied: number;
    canBeEditedRegionsCount: number;
}

export type GridAction =
    | CampaignsAction
    | GroupsAction
    | BannersAction
    | PhrasesAction
    | FeedFiltersAction
    | RetargetingsAction
    | UsersProfileAction;

/* Массовые действия над списком кампаний */
export enum CampaignsAction {
    resume = 'resumeCampaigns',
    stop = 'stopCampaigns',
    archive = 'archiveCampaigns',
    unarchive = 'unarchiveCampaigns',
    remoderate = 'remoderateCampaigns',
    copy = 'copyCampaigns',
    delete = 'deleteCampaigns',
    goToGroups = 'goToGroups',
    goToBanners = 'goToBanners',
    goToPhrases = 'goToPhrases',
    goToStatistics = 'goToCampaignsStatistics',
    goToLogs = 'goToLogs',
    goToCampaignModerationOffer = 'goToCampaignModerationOffer',
    addToFavorites = 'addToFavorites',
    removeFromFavorites = 'removeFromFavorites',
    payByCash = 'payByCash',
    exportToExcelWithBanners = 'exportToExcelWithBanners',
    exportToExcelWithoutBanners = 'exportToExcelWithoutBanners'
}

export enum GroupsAction {
    resume = 'resumeGroups',
    stop = 'stopGroups',
    delete = 'deleteGroups',
    sendBK = 'admGroupsSendBK',
    archive = 'archiveGroups',
    unarchive = 'unarchiveGroups',
    goToBanners = 'goToBanners',
    editGroups = 'editGroups',
    goToStatistics = 'goToGroupsStatistics',
    goToPhrases = 'goToPhrases',
    goToTargeting = 'goToTargeting',
    copy = 'copy',
    remoderateBannersAdditions = 'remoderateBannersAdditions',
    admSendMD = 'admSendMD',
    sendModerateByAgency = 'sendModerateByAgency',
    sendModerateByClient = 'sendModerateByClient',
    moderateAccept = 'moderateAccept',
    moderateAcceptBannersAdditions = 'moderateAcceptBannersAdditions',
    openPhrasesSelectorWizard = 'openPhrasesSelectorWizard',
    openPhrasesSelector = 'openPhrasesSelector',
    openMinusPhraseSelector = 'openMinusPhraseSelector',
    exportToExcelWithBanners = 'exportToExcelWithBanners',
    exportToExcelWithoutBanners = 'exportToExcelWithoutBanners',
    regionsTransform = 'regionsTransform'
}

export enum CampaignAction {
    addGroup = 'addGroup',
    editCampaigns = 'editCampaigns',
    editCampaignInDna = 'editCampaignInDna',
    goToStatistics = 'goToStatistics',
    goToCampaignModerationOffer = 'goToCampaignModerationOffer',
    exportToExcelWithBanners = 'exportToExcelWithBsanners',
    exportToExcelWithoutBanners = 'exportToExcelWithoutBanners',
    regionsTransform = 'regionsTransform',
    showParams = 'showParams'
}

export enum GroupAction {
    addBanner = 'addBanner',
    copy = 'copy',
    delete = 'deleteGroups',
    sendBK = 'admGroupsSendBK',
    editGroups = 'editGroups',
    editGroupsOld = 'editGroupsOld',
    remoderateBannersAdditions = 'remoderateBannersAdditions',
    admSendMD = 'admSendMD',
    sendModerateByAgency = 'sendModerateByAgency',
    sendModerateByClient = 'sendModerateByClient',
    moderateAccept = 'moderateAccept',
    moderateAcceptBannersAdditions = 'moderateAcceptBannersAdditions'
}

export enum BannersAction {
    stop = 'stopBanner',
    resume = 'resumeBanner',
    unarchive = 'unarchiveBanner',
    archive = 'archiveBanner',
    delete = 'deleteBanners',
    editBanners = 'editBanners',
    editBannersInternal = 'editBannersInternal',
    editPrices = 'editPrices',
    editYellowPagesData = 'editYandexCatalogData',
    exportToExcel = 'exportToExcel'
}

export enum BannerAction {
    editBanner = 'editBanner',
    editBannerOld = 'editBannerOld',
    aimBanner = 'aimBanner',
    internalBannerEdit = 'internalBannerEdit'
}

export enum DealAction {
    editDeal = 'editDeal'
}

export enum MinusPhraseLibraryAction {
    editMinusPhraseLibrary = 'editMinusPhraseLibrary',
    createByMinusPhraseLibrary = 'createByMinusPhraseLibrary',
    deleteMinusPhraseLibrary = 'deleteMinusPhraseLibrary'
}

export enum PhrasesAction {
    delete = 'deletePhrases',
    enable = 'enablePhrases',
    disable = 'disablePhrases',
    openSearchAndReplaceInspector = 'openSearchAndReplaceInspector',
    pricesWizard = 'pricesWizard',
    setPhrasesCommonPriceEditor = 'setPhrasesCommonPriceEditor'
}

export enum FeedFiltersAction {
    edit = 'editFeedFilters',
    delete = 'deleteFeedFilters',
    disable = 'disableFeedFilters',
    enable = 'enableFeedFilters',
    changeRate = 'changeFeedFiltersRate',
    changeAutobudget = 'changeFeedFiltersAutobudget'
}

export enum RetargetingsAction {
    setRetargetingCommonPriceEditor = 'setRetargetingCommonPriceEditor',
    resume = 'resumeRetargetings',
    suspend = 'suspendRetargetings',
    delete = 'deleteRetargetings'
}

export enum UsersProfileAction {
    setUsersProfileCommonPriceEditor = 'setUsersProfileCommonPriceEditor'
}

export enum pricePackagesAction {
    sendToApprovePricePackage = 'sendToApprovePricePackage',
    rejectPricePackage = 'rejectPricePackage',
    approvePricePackage = 'approvePricePackage',
    editPricePackage = 'editPricePackage'
}

export interface GridActionData {
    action: GridAction;
    type: GridActionItemType;
    disabled?: boolean;
    hidden?: boolean;
    tooltipText?: string;
}

export interface GridTooltipActionModerateFlags {
    action:
        | GroupsAction.delete
        | GroupsAction.sendBK
        | GroupsAction.remoderateBannersAdditions
        | GroupsAction.admSendMD
        | GroupsAction.sendModerateByAgency
        | GroupsAction.sendModerateByClient
        | GroupsAction.moderateAccept
        | GroupsAction.moderateAcceptBannersAdditions;
    isOverMaxSelection: boolean;
    isSelectionValid?: boolean;
    tooltip?: string;
}

export interface GridTooltipActionCopyFlags {
    action: GroupsAction.copy;
    isOverMaxSelection: boolean;
    canCopyGroups: boolean;
    canCopyCpmGroups: boolean;
    calcTypeFilterValue: CampaignsCalcTypeFilter;
    isSelectionValid?: boolean;
}

export interface GridTooltipDisabledAction {
    enabled: boolean;
    tooltipText?: string;
}

export interface GridTooltipActionGroupMinusPhraseSelectorFlags {
    action: GroupsAction.openMinusPhraseSelector;
    isOverMaxSelection: boolean;
    canSelectedCampaignTypesManageLibraryMinusPhrase: boolean;
    maxSelectionLength: number;
    canManageLibraryMinusPhrase: boolean;
}

export interface GridTooltipActionGroupRegionsTransform {
    action: GroupsAction.regionsTransform;
    isOverMaxSelection: boolean;
    canEditRegions: boolean;
    accessForEditRegions: boolean;
}

export type GridTooltipActionGroupFlags =
    | GridTooltipActionGroupMinusPhraseSelectorFlags
    | GridTooltipActionCopyFlags
    | GridTooltipActionModerateFlags
    | GridTooltipActionGroupRegionsTransform;

export interface GridTooltipActionCampaignRegionsTransform {
    action: CampaignAction.regionsTransform;
    isCampaignEditDisabled: boolean;
    accessForEditRegions: boolean;
}

export type GridTooltipActionCampaignFlags = GridTooltipActionCampaignRegionsTransform;

/* Набор actions для элементов таблицы списка кампаний */

export type GridItemBaseActions = {
    onOpenRecommendationsPopup: () => void;
    onSubmitRecommendation: (
        id: string,
        type: RecommendationType,
        keys: Array<RecommendationKey>,
        itemName: string
    ) => void;
    onShowRecommendation: (view: RecommendationCardView, type: RecommendationType) => void;
};

export type GridCampaignItemActions = {
    onGroupAdd: (campaign: SelectorItems, entityToSelectType: CampaignAndGroupSelectorEntityToSelectType) => void;
    onEdit: (campaignId: string, login: string) => void;
    onEditInDna: (campaignId: string, login: string, campaignType: CampaignType) => void;
    onAddSuccess: (data: SaveSuccessData) => void;
    onGoToStatistics: (campaignId: string) => void;
    onGoToCampaignModerationOffer: (campaignId: string) => void;
    onRegionsTransformEditorOpen: (campaignId: string) => void;
    onShowParams: (campaignId: string, login: string, campaignType: CampaignType) => void;
} & GridItemBaseActions;

export type GridGroupItemActions = {
    onBannerAdd: (
        campaign: SelectorItems,
        group: SelectorItems,
        entityToSelectType: CampaignAndGroupSelectorEntityToSelectType
    ) => void;
    onEdit: (groupIds: string[], campaignId: string, campaignType: CampaignType, login: string) => void;
    onEditInDna: (login: string, campaignId: string, groupId: string) => void;
    onCampaignEdit: (campaignId: string, login: string) => void;
    onAddSuccess: (data: SaveSuccessData) => void;
    getModerationRejectReasons: (groupId: string) => void;
    onHandleAction: (id: string, action: GroupsAction) => void;
} & GridItemBaseActions;

export type GridBannerItemActions = {
    runBannerAim: (login: string, bannerId: string, cookieTtl: number) => void;
    onBannerAim: (payload?: BannerAimingPopupData) => void;
    onGroupEdit: (groupId: string, campaignId: string, campaignType: CampaignType, login: string) => void;
    onBannerEdit: (
        groupId: string,
        campaignId: string,
        campaignType: CampaignType,
        login: string,
        bannerId: string
    ) => void;
    onBannerEditDna: (login: string, campaignId: string, bannerId: string) => void;
    onInternalBannerEdit: (login: string, campaignId: string, bannerId: string) => void;
    onSendLicenseButtonClick: () => void;
    showBannersPreview: (data: PcodeRenderData, livePreviewUrl?: string) => void;
    showBannerCreativePreview: (data: PcodeRenderData, livePreviewUrl: string) => void;
    getModerationRejectReasons: (bid: string) => void;
} & GridItemBaseActions;

export type GridPhraseItemActions = {
    updatePhrase: (phrase: PhraseChange, columnName: string) => void;
};

export type GridRetargetingItemActions = {
    onHandleAction: (id: string, action: RetargetingsAction) => void;
};

export type GridFeedFilterItemActions = {
    onFilterEdit: (groupId: string, feedId: string, filter: EditGroupFilterItem) => void;
};

export type LogItemAction = {
    onExpandRow: (gridExpansion: GridExpansion) => void;
    onHeightCalculated: (gridMaxHeight: GridMaxHeight) => void;
    onHeightRecalculated: () => void;
    onEntityClick?: (params: LogNavEntityParams) => void;
};

/* Набор actions для элементов внутри таблицы */
export type GridItemActions =
    | GridCampaignItemActions
    | GridGroupItemActions
    | GridBannerItemActions
    | GridPhraseItemActions
    | GridFeedFilterItemActions
    | GridRetargetingItemActions
    | MinusPhraseLibraryActions
    | LogItemAction
    | DealItemAction
    | PricePackagesMenuActions;

export interface StatusHint {
    text: string;
    link?: string;
    clickHandler?: () => void;
}

export interface GridMaxHeight {
    rowIndex: number;
    maxHeight?: number;
}

export interface GridExpansion {
    rowIndex: number;
    isExpanded: boolean;
}

export const defaultRowsCount = 100;

export enum GridEditedEntityType {
    Campaign = 'campaign',
    Group = 'group',
    Banner = 'banner',
    Phrase = 'phrase'
}

export type GridEditedEntity =
    | {
          type: GridEditedEntityType.Campaign;
          id?: string;
          campaignType?: CampaignType;
          login: string;
          anchor?: string;
      }
    | {
          type: GridEditedEntityType.Group;
          groupIds?: string[];
          bannerIds?: string[];
          campaignId: string;
          campaignType: CampaignType;
          login: string;
          anchor?: string;
      }
    | {
          type: GridEditedEntityType.Banner;
          groupId: string;
          campaignId: string;
          campaignType: CampaignType;
          login: string;
          bannerId: string;
          anchor: string;
      }
    | {
          type: GridEditedEntityType.Phrase;
          groupId: string;
          campaignId: string;
          campaignType: CampaignType;
          login: string;
          bannerId: string;
          anchor: string;
      };

export interface GridCampaignAddData {
    type: GridEditedEntityType.Campaign;
    campaignType: CampaignType;
    login: string;
    isCampaignAddAllowedInDna: boolean;
}

export interface GridGroupAddData {
    type: GridEditedEntityType.Group;
    campaignId: string;
    campaignType: CampaignType;
    login: string;
}

export interface GridBannerAddData {
    type: GridEditedEntityType.Banner;
    campaignId: string;
    campaignType: CampaignType;
    groupId: string;
    login: string;
}

export interface GridPhraseAddData {
    type: GridEditedEntityType.Phrase;
    campaignId: string;
    campaignType: CampaignType;
    groupId: string;
    login: string;
}

export interface GridCacheKeys {
    campaigns?: string;
    groups?: string;
    banners?: string;
    showConditions?: string;
    feedFilters?: string;
}

export type GridEditCells = Record<string, GridEditCellStatus>;

export interface GridEditCellStatus {
    isEditInProgress?: boolean;
    isSaving: boolean;
    error?: string | ErrorItem[];
}

export type GridTabMenuLinkData = NavLinkData & {
    hint?: string;
    tooltip?: string;
};

export type GridEditedEntityAddData = GridCampaignAddData | GridGroupAddData | GridBannerAddData | GridPhraseAddData;

export interface GridTotalStatsInfo {
    cost?: string;
    shows?: string;
    clicks?: string;
    ctr?: string;
}

export interface PayActionInfo {
    campaignTypes: GdCampaignType[];
    unsupportedCampaigns: PayActionCampaign[];
}

export interface GridCellProps<T, E = {}> {
    rowData: T;
    rowIndex: number;
    actions: E;
}

export interface GridLocalStorageData {
    campaignsIds?: Array<string>;
    groupsIds?: Array<string>;
    goalsSettings?: GoalDecl[];
    alertMessageFilter?: FilterGridViewDict;
}

export interface GridInitialData {
    filterDict?: FilterGridViewDict;
    goalsSettings?: GoalDecl[];
    campaignsCalcTypeFilter: CampaignsCalcTypeFilter;
}

export enum GridRouterParams {
    campaigns = 'campaigns',
    groups = 'groups',
    banners = 'banners'
}
